/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import type { Theme } from '@coursera/cds-core';
import { Hidden } from '@coursera/cds-core';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import { CalendarLoader } from 'bundles/degree-home/components/v3-dpm/components/Calendar';
import { CalendarDayPicker } from 'bundles/degree-home/components/v3-dpm/components/Calendar/CalendarDayPicker';
import { CalendarFilter } from 'bundles/degree-home/components/v3-dpm/components/Calendar/CalendarFilter';
import { CalendarItemsList } from 'bundles/degree-home/components/v3-dpm/components/Calendar/CalendarItemsList';
import { getCalendarEventsMap } from 'bundles/degree-home/components/v3-dpm/components/Calendar/utils/index';
import { Card } from 'bundles/program-loggedin-shared/components/Card';

import type { UseCalendarEventsQuery as UseCalendarEventsQueryData } from '../../lib/__queries__/__generated__/useCalendarEvents';
import useCalendarEvents from '../../lib/useCalendarEvents';

const styles = {
  card: (theme: Theme) => css`
    overflow: hidden;
    padding: ${theme.spacing(8, 0, 32, 0)};
  `,
};

type CardProps = {
  degreeId: string;
};

type ContentProps = {
  loading: boolean;
  data?: UseCalendarEventsQueryData;
  error: Error | undefined | null;
  refetch: () => void;
};

export const CalendarCardContent: React.FunctionComponent<ContentProps> = ({ loading, data, error, refetch }) => {
  const { setRefetchCalendarItems, setCalendarItemsLoading, setCalendarEvents, setRegistrationDates } =
    useDegreeContext();

  useEffect(() => {
    const events = data?.Degree?.queryById?.events;

    if (events) {
      setCalendarEvents(getCalendarEventsMap(events));
    }

    setRefetchCalendarItems(() => refetch);
  }, [data, refetch, setCalendarEvents, setRefetchCalendarItems, setRegistrationDates]);

  useEffect(() => {
    setCalendarItemsLoading(loading);
    // We only want to call this effect when the loading changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (!data && loading) {
    return <CalendarLoader />;
  }

  if (error) {
    return null;
  }

  return (
    <Card css={styles.card}>
      <CalendarFilter />
      <CalendarDayPicker />
      <CalendarItemsList />
    </Card>
  );
};

export const CalendarCard: React.FunctionComponent<CardProps> = ({ degreeId }) => {
  const { setDegreeId } = useDegreeContext();
  useEffect(() => {
    setDegreeId(degreeId);
  }, [degreeId, setDegreeId]);

  const calendarEventsGql = useCalendarEvents();

  if (!calendarEventsGql) {
    return null;
  }

  const { data, loading, error, refetch } = calendarEventsGql;

  return (
    <Hidden smDown>
      <CalendarCardContent loading={loading} data={data} error={error} refetch={refetch} />
    </Hidden>
  );
};
